// The Fleet page's removals (#6563 review): the one the details panel drives now, and
// every removal whose token cascade failed, parked on the PAGE (coordinator decision
// 2026-10-22). A failed cascade is a machine that IS removed whose access tokens are not
// all revoked; it leaves the roster on the re-read, and the panel may already show another
// machine or be shut. So the warning and its retry cannot live in the panel: each one
// stays here, named and with its own retry, until that retry succeeds or the person
// dismisses it. Nothing here reopens the panel or moves focus.
//
// ONE RETRY IMPLEMENTATION: a parked entry IS the `createMachineRemove` that produced it
// (`./machine-remove`), so "Retry revoking" is its `retry` (revoke, never a second
// decommission) and a clean retry ends through the same `onRemoved` as a clean removal.
// The panel gets a fresh controller the moment one is parked.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { decommissionBox } from './box-lifecycle';
import { revokeRunner } from './fleet';
import type { MachinePanel } from './machine-panel';
import { createMachineRemove, type MachineRemove, type RemovableMachine } from './machine-remove';

/** One parked removal: the machine it removed, and the controller that holds its retry. */
export type ParkedRemoval = { readonly id: string; readonly remove: MachineRemove };

export type MachineRemovals = {
  /** The removal the details panel drives now. */
  readonly current: () => MachineRemove;
  /** Removals whose token cascade failed, in the order they failed, one per machine. */
  readonly parked: () => readonly ParkedRemoval[];
  /** "Dismiss": drop one parked warning without retrying. */
  readonly dismiss: (id: string) => void;
};

export function createMachineRemovals(input: {
  readonly token: string | null;
  /** Which machine the panel shows, and how to shut it. */
  readonly panel: Pick<MachinePanel, 'id' | 'close'>;
  readonly notify: (message: string) => void;
  /** Any landed write: re-read the roster. */
  readonly onWritten: () => void;
}): MachineRemovals {
  const [parked, setParked] = createSignal<readonly ParkedRemoval[]>([]);
  const drop = (id: string): void => {
    setParked((list) => list.filter((p) => p.id !== id));
  };

  // The panel shuts only if it still shows the removed machine: a removal can land after
  // the person moved to another one, and that one stays open. The toast names the machine
  // the removal was fired on (captured when Remove was pressed).
  const leave = (m: RemovableMachine): void => {
    if (input.panel.id() === m.id) input.panel.close();
  };

  const make = (): MachineRemove => {
    const remove: MachineRemove = createMachineRemove({
      decommission: (id) => decommissionBox(id, input.token),
      revoke: (id) => revokeRunner(id, input.token),
      onWritten: input.onWritten,
      onRemoved: (m) => {
        drop(m.id);
        input.notify(t('fleet.panel.removed_toast', { name: m.display_name }));
        leave(m);
      },
      onCascadeFailed: (m) => {
        setParked((list) => [...list.filter((p) => p.id !== m.id), { id: m.id, remove }]);
        setCurrent(make);
        leave(m);
      },
    });
    return remove;
  };
  const [current, setCurrent] = createSignal<MachineRemove>(make());

  return { current, parked, dismiss: drop };
}
